/**
 * Mexico 68 echo lines. Every ring is an exact offset of the mark and of one
 * rounded box (a button, a heading block, a form) together: a distance field
 * of the mark, computed once per size, plus the box's rounded rectangle,
 * banded into line and gap at one even step and drawn in a single WebGL pass.
 * Without WebGL2 the host is simply left on its own background.
 *
 * The mark may hang off the edge of the host (the page heads crop it); its
 * distance field is computed over the union of both, so the rings stay true
 * right up to the edge.
 *
 * Ported from the website (superelectric-site/src/scripts/rings.ts); the two
 * are separate packages. This copy returns a disposer for component unmount.
 */
import { MARK_PATH } from '../constants/brandMark';

export interface RingOptions {
  /** Element the canvas covers. Must be positioned; its background shows when WebGL is missing. */
  host: HTMLElement;
  canvas: HTMLCanvasElement;
  /** The <svg> that draws the mark. Its box is the 216-unit grid. */
  mark: Element;
  /** Rounded box the rings also echo. */
  box?: HTMLElement | null;
  /** Corner radius of that box, in CSS px. */
  boxRadius?: number;
  /** Clear space kept around the box before the first ring, in CSS px. */
  boxGap?: number;
  /** Line (= gap) width in mark units: the mark is 216 across. */
  step?: number;
  /** Send one pulse outward once drawn, like the shock of a key striking. */
  pulse?: boolean;
  /** Hovering or focusing this element sends the box's rings out by one line. */
  hover?: HTMLElement | null;
  /** Ground and line colors. */
  ground?: string;
  line?: string;
  /** Upper bound on distance-field pixels; lower keeps phones quick. */
  maxPixels?: number;
}

const hex = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);

// Exact squared Euclidean distance transform (Felzenszwalb & Huttenlocher), in place.
function edt(g: Float64Array, w: number, h: number) {
  const m = Math.max(w, h);
  const f = new Float64Array(m), d = new Float64Array(m), v = new Int32Array(m), z = new Float64Array(m + 1);
  const pass = (n: number) => {
    let k = 0, r: number, s: number;
    v[0] = 0; z[0] = -Infinity; z[1] = Infinity;
    for (let q = 1; q < n; q++) {
      r = v[k]; s = (f[q] + q * q - f[r] - r * r) / (2 * q - 2 * r);
      while (s <= z[k]) { k--; r = v[k]; s = (f[q] + q * q - f[r] - r * r) / (2 * q - 2 * r); }
      k++; v[k] = q; z[k] = s; z[k + 1] = Infinity;
    }
    for (let q = 0, k2 = 0; q < n; q++) {
      while (z[k2 + 1] < q) k2++;
      r = v[k2]; d[q] = (q - r) * (q - r) + f[r];
    }
  };
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) f[y] = g[y * w + x];
    pass(h);
    for (let y = 0; y < h; y++) g[y * w + x] = d[y];
  }
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) f[x] = g[y * w + x];
    pass(w);
    for (let x = 0; x < w; x++) g[y * w + x] = d[x];
  }
}

const FRAG = `#version 300 es
precision highp float;
uniform sampler2D u_sdf;uniform vec2 u_size;uniform vec4 u_btn;uniform vec4 u_field;
uniform float u_dpr,u_step,u_btnR,u_gap,u_phase,u_pulse;
uniform vec3 u_teal,u_cream,u_coral,u_coralL;
out vec4 o;
void main(){
  vec2 p=vec2(gl_FragCoord.x,u_size.y*u_dpr-gl_FragCoord.y)/u_dpr;
  float dm=texture(u_sdf,(p-u_field.xy)/u_field.zw).r;
  vec2 q=abs(p-u_btn.xy)-u_btn.zw+u_btnR;
  float db=length(max(q,0.))+min(max(q.x,q.y),0.)-u_btnR;
  float s=u_step,d=min(dm,db-u_gap-u_phase),aa=max(fwidth(d),.001);
  float tri=abs(fract((d-1.5*s)/(2.*s)+.5)-.5)*2.*s;
  float line=(1.-smoothstep(.5*s-.5*aa,.5*s+.5*aa,tri))*smoothstep(.75*s-.5*aa,.75*s+.5*aa,d);
  vec3 c=mix(u_cream,u_coralL,exp(-pow((d-u_pulse)/(2.5*s),2.)));
  vec3 col=(db-u_gap)<0.?u_teal:mix(u_teal,c,line);
  o=vec4(mix(col,u_coral,1.-smoothstep(-.5*aa,.5*aa,dm)),1.);
}`;

export function mountRings(opts: RingOptions): () => void {
  const { host, canvas: cv, mark: markEl, box = null, hover = null } = opts;
  const STEP = opts.step ?? 7;
  const gl = cv.getContext('webgl2', { alpha: false, antialias: false });
  if (!gl) return () => {};
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const markPath = new Path2D(MARK_PATH);

  const shader = (type: number, src: string) => {
    const o = gl.createShader(type)!;
    gl.shaderSource(o, src);
    gl.compileShader(o);
    return o;
  };
  const prog = gl.createProgram()!;
  gl.attachShader(prog, shader(gl.VERTEX_SHADER,
    '#version 300 es\nvoid main(){gl_Position=vec4(gl_VertexID==1?3.:-1.,gl_VertexID==2?3.:-1.,0.,1.);}'));
  gl.attachShader(prog, shader(gl.FRAGMENT_SHADER, FRAG));
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return () => {};
  gl.useProgram(prog);
  const U: Record<string, WebGLUniformLocation | null> = {};
  ['sdf', 'size', 'btn', 'field', 'dpr', 'step', 'btnR', 'gap', 'phase', 'pulse', 'teal', 'cream', 'coral', 'coralL']
    .forEach((n) => (U[n] = gl.getUniformLocation(prog, 'u_' + n)));
  gl.uniform3fv(U.teal, hex(opts.ground ?? '#1f7a72'));
  gl.uniform3fv(U.cream, hex(opts.line ?? '#f0e3c4'));
  gl.uniform3fv(U.coral, hex('#ff6b45'));
  gl.uniform3fv(U.coralL, hex('#ffab8a'));
  gl.uniform1i(U.sdf, 0);
  gl.uniform1f(U.btnR, opts.boxRadius ?? 6);
  gl.uniform1f(U.gap, opts.boxGap ?? 0);
  gl.bindTexture(gl.TEXTURE_2D, gl.createTexture());
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

  type State = { w: number; h: number; dpr: number; step: number; far: number; btn: number[]; field: number[] };
  let S: State | null = null;
  let phase = 0;
  let pulseAt = -1e5;
  let disposed = false;

  const draw = () => {
    if (!S || disposed) return;
    gl.viewport(0, 0, cv.width, cv.height);
    gl.uniform2f(U.size, S.w, S.h);
    gl.uniform1f(U.dpr, S.dpr);
    gl.uniform1f(U.step, S.step);
    gl.uniform4fv(U.btn, S.btn);
    gl.uniform4fv(U.field, S.field);
    gl.uniform1f(U.phase, phase * 2 * S.step);
    gl.uniform1f(U.pulse, pulseAt);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  };

  const build = () => {
    const H = host.getBoundingClientRect(), M = markEl.getBoundingClientRect();
    if (disposed || !H.width || !H.height || !M.width) return;
    const dprMax = Math.min(devicePixelRatio || 1, 2);
    cv.width = Math.round(H.width * dprMax);
    cv.height = Math.round(cv.width * H.height / H.width);
    // The distance field covers the host plus any part of the mark hanging off it.
    const fx = Math.min(0, M.left - H.left), fy = Math.min(0, M.top - H.top);
    const fw = Math.max(H.width, M.right - H.left) - fx, fh = Math.max(H.height, M.bottom - H.top) - fy;
    // Rasterize the mark at up to device resolution (at most ~2M px, so big screens stay quick), then take its signed distance.
    const k = Math.min(dprMax, Math.sqrt((opts.maxPixels ?? 2e6) / (fw * fh))), w = Math.ceil(fw * k), h = Math.ceil(fh * k);
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const x = c.getContext('2d', { willReadFrequently: true })!, sc = M.width / 216;
    x.setTransform(sc * k, 0, 0, sc * k, (M.left - H.left - fx) * k, (M.top - H.top - fy) * k);
    x.fill(markPath, 'evenodd');
    const a = x.getImageData(0, 0, w, h).data, n = w * h;
    const fo = new Float64Array(n), fi = new Float64Array(n), sd = new Float32Array(n);
    for (let i = 0; i < n; i++) { const inside = a[i * 4 + 3] > 127; fo[i] = inside ? 0 : 1e20; fi[i] = inside ? 1e20 : 0; }
    edt(fo, w, h); edt(fi, w, h);
    for (let i = 0; i < n; i++) {
      const al = a[i * 4 + 3] / 255; // antialiased edge pixels know their own sub-pixel distance
      sd[i] = (al > 0 && al < 1 ? 0.5 - al : fo[i] > 0 ? Math.sqrt(fo[i]) - 0.5 : 0.5 - Math.sqrt(fi[i])) / k;
    }
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R16F, w, h, 0, gl.RED, gl.FLOAT, sd);
    const cx = M.left - H.left + M.width / 2, cy = M.top - H.top + M.height / 2;
    const B = box?.getBoundingClientRect();
    S = {
      w: H.width, h: H.height, dpr: cv.width / H.width, step: STEP * sc,
      far: Math.max(Math.hypot(cx, cy), Math.hypot(H.width - cx, cy), Math.hypot(cx, H.height - cy), Math.hypot(H.width - cx, H.height - cy)),
      btn: B ? [B.left - H.left + B.width / 2, B.top - H.top + B.height / 2, B.width / 2, B.height / 2] : [-1e5, -1e5, 0, 0],
      field: [fx, fy, fw, fh],
    };
    draw();
  };

  const pulse = () => {
    const t0 = performance.now();
    const f = (now: number) => {
      if (!S || disposed) return;
      const t = Math.min(1, (now - t0) / 1400);
      pulseAt = t < 1 ? -3 * S.step + (S.far + 6 * S.step) * (1 - (1 - t) * (1 - t)) : -1e5;
      draw();
      if (t < 1) requestAnimationFrame(f);
    };
    requestAnimationFrame(f);
  };

  // Built once the fonts settle, since the box's size (and so the rings around it) depends on them.
  (document.fonts ? document.fonts.ready : Promise.resolve()).then(() => {
    build();
    if (opts.pulse && !reduced && S) pulse();
  });
  let rt = 0;
  const later = () => { clearTimeout(rt); rt = window.setTimeout(build, 120); };
  addEventListener('resize', later);
  // A box that grows (an error message, a longer form) moves its rings with it.
  let ro: ResizeObserver | null = null;
  if (box && 'ResizeObserver' in window) {
    let first = true;
    ro = new ResizeObserver(() => { if (first) { first = false; return; } later(); });
    ro.observe(box);
  }

  let target = 0, raf = 0;
  const tick = () => {
    phase += (target - phase) * 0.16;
    if (Math.abs(target - phase) < 0.005) phase = target;
    draw();
    raf = phase !== target && !disposed ? requestAnimationFrame(tick) : 0;
  };
  const enter = () => { target = 1; if (!raf) raf = requestAnimationFrame(tick); };
  const leave = () => { target = 0; if (!raf) raf = requestAnimationFrame(tick); };
  if (hover && !reduced) {
    // Hovering sends the box's rings out by one line: a new ring is struck at its edge.
    hover.addEventListener('pointerenter', enter);
    hover.addEventListener('pointerleave', leave);
    hover.addEventListener('focus', enter);
    hover.addEventListener('blur', leave);
  }

  return () => {
    disposed = true;
    clearTimeout(rt);
    cancelAnimationFrame(raf);
    removeEventListener('resize', later);
    ro?.disconnect();
    if (hover) {
      hover.removeEventListener('pointerenter', enter);
      hover.removeEventListener('pointerleave', leave);
      hover.removeEventListener('focus', enter);
      hover.removeEventListener('blur', leave);
    }
    gl.getExtension('WEBGL_lose_context')?.loseContext();
  };
}
